import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { logQuietError } from "@/lib/quiet-error";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth-context";
import {
  Search,
  Globe,
  Sparkles,
  Eye,
  Copy as CopyIcon,
  ArrowRight,
  TrendingUp,
  Compass,
  Filter,
  LogIn,
} from "lucide-react";
import { SkeletonRow } from "@/components/Skeleton";
import { toast } from "sonner";

const SORTS = [
  { v: "featured",   label: "Featured",     icon: Sparkles },
  { v: "recent",     label: "Recent",       icon: Compass },
  { v: "popular",    label: "Most viewed",  icon: TrendingUp },
  { v: "duplicates", label: "Most copied",  icon: CopyIcon },
];

export default function Explore() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [items, setItems] = useState(null);
  const [facets, setFacets] = useState({ subjects: [], grades: [], languages: [], curricula: [] });
  const [filters, setFilters] = useState({ subject: "", grade: "", language: "", curriculum: "" });
  const [sort, setSort] = useState("featured");
  const [q, setQ] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/explore/facets");
        setFacets(data);
      } catch (e) { logQuietError("explore.facets", e); }
    })();
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      setItems(null);
      const params = new URLSearchParams();
      if (filters.subject)    params.set("subject", filters.subject);
      if (filters.grade)      params.set("grade", filters.grade);
      if (filters.language)   params.set("language", filters.language);
      if (filters.curriculum) params.set("curriculum", filters.curriculum);
      if (sort)               params.set("sort", sort);
      if (q.trim())           params.set("q", q.trim());
      try {
        const { data } = await api.get(`/explore/lessons?${params.toString()}`);
        if (!alive) return;
        setItems(data);
      } catch (e) {
        logQuietError("explore.lessons", e);
        if (alive) setItems([]);
      }
    })();
    return () => { alive = false; };
  }, [filters, sort, q]);

  const duplicate = async (token, title) => {
    if (!user) { return navigate("/signup?next=/explore"); }
    try {
      const { data } = await api.post(`/public/lessons/${token}/duplicate`);
      toast.success(`Saved "${title}" to your workspace`);
      navigate(`/lesson/${data.lesson_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not duplicate");
    }
  };

  const remix = async (token, title) => {
    if (!user) { return navigate("/signup?next=/explore"); }
    try {
      toast.info("Remixing for your class…");
      const { data } = await api.post(`/remix`, { share_token: token, save_lesson: true });
      toast.success(`Remix ready · ${data.coins_spent} coins used`);
      if (data.new_lesson_id) navigate(`/lesson/${data.new_lesson_id}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.error === "INSUFFICIENT_COINS") {
        toast.error(`Need ${detail.required} coins · You have ${detail.balance}`);
      } else {
        toast.error(typeof detail === "string" ? detail : "Remix failed");
      }
    }
  };

  return (
    <Layout title="Explore lessons" anonymous={!user && !authLoading}>
      {!user && !authLoading && (
        <div className="tb-card-feature relative overflow-hidden mb-5" data-testid="explore-anon-cta">
          <div className="absolute inset-0 tb-grid-bg opacity-50 pointer-events-none" />
          <div className="relative flex flex-col sm:flex-row sm:items-center gap-3">
            <span className="h-10 w-10 rounded-xl tb-ai-orb grid place-items-center shrink-0">
              <Sparkles className="h-5 w-5 text-white" />
            </span>
            <div className="flex-1 min-w-0">
              <p className="font-display text-lg font-medium tracking-tight">Browsing as a guest</p>
              <p className="text-[12px] text-muted-foreground">Sign up free to remix any lesson, save to your workspace, and unlock AI tools.</p>
            </div>
            <Button onClick={() => navigate("/signup?next=/explore")} className="rounded-full" data-testid="explore-anon-signup">
              <LogIn className="h-3.5 w-3.5 mr-1.5" /> Sign up free
            </Button>
          </div>
        </div>
      )}
      <div className="mb-6">
        <span className="tb-chip-sky inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold tracking-wide">
          <Globe className="h-3 w-3" /> Community library
        </span>
        <h1 className="mt-3 font-display text-3xl sm:text-4xl tracking-tight font-medium leading-tight">
          Lessons shared by teachers everywhere
        </h1>
        <p className="mt-1.5 text-sm text-muted-foreground max-w-2xl">
          Discover, preview, and duplicate ready-to-teach lessons into your own workspace with one tap.
        </p>
      </div>

      {/* Search + sort */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4 items-stretch sm:items-center">
        <div className="relative flex-1 max-w-lg">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by title…" className="pl-9 h-11 rounded-full" data-testid="explore-search" />
        </div>
        <div className="flex gap-2">
          {SORTS.map((s) => {
            const Icon = s.icon;
            const active = sort === s.v;
            return (
              <button
                key={s.v}
                onClick={() => setSort(s.v)}
                className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium transition border ${
                  active ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:border-primary/40"
                }`}
                data-testid={`explore-sort-${s.v}`}
              >
                <Icon className="h-3.5 w-3.5" /> {s.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Facets */}
      <div className="flex flex-wrap gap-2 mb-6 items-center text-xs">
        <span className="inline-flex items-center gap-1 text-muted-foreground"><Filter className="h-3 w-3" /> Filters:</span>
        <FacetSelect label="Subject"    value={filters.subject}    options={facets.subjects}  onChange={(v) => setFilters((f) => ({ ...f, subject: v }))} testid="facet-subject" />
        <FacetSelect label="Grade"      value={filters.grade}      options={facets.grades}    onChange={(v) => setFilters((f) => ({ ...f, grade: v }))}   testid="facet-grade" />
        <FacetSelect label="Language"   value={filters.language}   options={facets.languages} onChange={(v) => setFilters((f) => ({ ...f, language: v }))} testid="facet-language" />
        <FacetSelect label="Curriculum" value={filters.curriculum} options={facets.curricula} onChange={(v) => setFilters((f) => ({ ...f, curriculum: v }))} testid="facet-curriculum" />
        {(filters.subject || filters.grade || filters.language || filters.curriculum) && (
          <button
            onClick={() => setFilters({ subject: "", grade: "", language: "", curriculum: "" })}
            className="ml-2 text-[11px] text-primary hover:underline"
            data-testid="explore-clear-filters"
          >
            Clear all
          </button>
        )}
      </div>

      {items === null ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => <div key={i} className="tb-card p-5"><SkeletonRow /></div>)}
        </div>
      ) : items.length === 0 ? (
        <div className="tb-card p-10 text-center" data-testid="explore-empty">
          <span className="mx-auto h-14 w-14 grid place-items-center rounded-full border border-dashed border-border mb-3">
            <Globe className="h-6 w-6 text-muted-foreground" />
          </span>
          <p className="font-display text-lg font-medium">No lessons match these filters</p>
          <p className="mt-1 text-sm text-muted-foreground">Try removing a filter or browsing recent lessons.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 tb-stagger">
          {items.map((it) => (
            <div
              key={it.share_token}
              className="tb-card-interactive p-5 group"
              data-testid={`explore-card-${it.share_token}`}
            >
              <button onClick={() => navigate(`/l/${it.share_token}`)} className="text-left w-full">
                <div className="flex flex-wrap items-center gap-2">
                  {it.is_featured && (
                    <span
                      className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-amber-200 to-amber-100 dark:from-amber-500/30 dark:to-amber-500/10 text-amber-900 dark:text-amber-200 border border-amber-300/60 dark:border-amber-500/30 text-[10px] px-2 py-0.5 font-semibold uppercase tracking-wide"
                      data-testid={`explore-featured-${it.share_token}`}
                    >
                      <Sparkles className="h-2.5 w-2.5" /> TeeBee Official
                    </span>
                  )}
                  <span className="tb-chip-lavender rounded-full text-[10px] px-2 py-0.5 font-medium uppercase tracking-wide">{it.subject || "General"}</span>
                  {it.grade && <span className="tb-chip-sky rounded-full text-[10px] px-2 py-0.5 font-medium">Grade {it.grade}</span>}
                </div>
                <p className="mt-3 font-display font-medium text-base leading-snug line-clamp-2">{it.title}</p>
                <p className="mt-1 text-[11px] text-muted-foreground line-clamp-1">
                  {[it.author_name, it.institution_name, it.language].filter(Boolean).join(" · ") || "Anonymous teacher"}
                </p>
                <div className="mt-4 flex items-center gap-3 text-[11px] text-muted-foreground">
                  <span className="inline-flex items-center gap-1"><Eye className="h-3 w-3" /> {it.view_count}</span>
                  <span className="inline-flex items-center gap-1"><CopyIcon className="h-3 w-3" /> {it.duplicate_count}</span>
                  <span className="inline-flex items-center gap-1"><Sparkles className="h-3 w-3" /> {it.artifact_count} artifacts</span>
                </div>
              </button>
              <div className="mt-4 flex gap-2">
                <Button size="sm" variant="outline" className="rounded-full flex-1" onClick={() => navigate(`/l/${it.share_token}`)} data-testid={`explore-preview-${it.share_token}`}>
                  Preview
                </Button>
                <Button size="sm" className="rounded-full flex-1" onClick={() => remix(it.share_token, it.title)} data-testid={`explore-remix-${it.share_token}`}>
                  <Sparkles className="h-3.5 w-3.5 mr-1.5" /> Remix
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Layout>
  );
}

function FacetSelect({ label, value, options, onChange, testid }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="h-8 rounded-full border border-border bg-card px-3 text-xs hover:border-primary/40">
      {/* Single string child — multi-child text content (e.g. `{label} (any)`) makes
          some React versions emit a hydration warning inside <option> on certain
          browser engines. Joining to one string sidesteps that entirely. */}
      <option value="">{`${label} (any)`}</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}
